/**
 * The Shopify Forms app embed renders inside an open shadow root, which
 * blocks regular page CSS. Its documented custom properties (set on
 * `snippets/forms-app-overrides.liquid`) cover colors/fonts/radii, but a
 * few things (the section legends, the checkbox, the submit button's
 * font/padding) have no exposed property, so we inject a stylesheet
 * directly into the shadow root instead.
 */
function injectFormsStyles(root) {
  if (root.querySelector('#forms-app-theme-overrides')) return;

  const style = document.createElement('style');
  style.id = 'forms-app-theme-overrides';
  style.textContent = `
    section[class*="_formContainer_"],
    section[class*="_inline_"] {
      overflow: visible !important;
    }

    /* The form is a grid item, which by default can't shrink below its
       content's width (~340px). On phones that is wider than the card it sits
       in, so the right side was clipped. Let it shrink to the card instead. */
    section[class*="_formContainer_"] {
      min-width: 0 !important;
    }

    /* Same for the phone row: its input keeps the browser's default size
       (~216px) next to the country picker, which pushed it out of the card. */
    div[class*="_formPhoneInputContainer_"] {
      width: 100% !important;
    }

    div[class*="_formPhoneInputContainer_"] > div[class*="_formFieldContainer_"] {
      flex: 1 1 0 !important;
      min-width: 0 !important;
    }

    input[class*="_formPhoneInputField_"] {
      width: 100% !important;
      min-width: 0 !important;
    }

    /* The heading and intro copy now live in the theme's own left-side
       column (real H2/paragraph type presets), so hide the app's
       duplicate built-in header. */
    section[class*="_formHeader_"] {
      display: none !important;
    }

    legend[class*="_textBody_"] {
      display: block;
      font-family: var(--font-h3--family) !important;
      font-style: var(--font-h3--style) !important;
      font-weight: var(--font-h3--weight) !important;
      font-size: clamp(18px, 2.34vw, 30px) !important;
      line-height: 1.3;
      letter-spacing: var(--font-h3--letter-spacing);
      text-transform: var(--font-h3--case);
      color: var(--color-foreground);
      margin-top: clamp(32px, 3.75vw, 48px) !important;
      margin-bottom: clamp(8px, 0.9vw, 12px) !important;
    }

    label[class*="_checkboxField_"] {
      display: inline-flex;
      align-items: center;
      gap: var(--padding-2xs);
      cursor: pointer;
    }

    input[class*="_checkboxInput_"] {
      width: var(--checkbox-size);
      height: var(--checkbox-size);
      aspect-ratio: 1;
      margin: 0;
      padding: 0;
      border: var(--checkbox-border);
      border-radius: var(--checkbox-border-radius);
      appearance: none;
      position: relative;
      background-color: var(--color-background);
      cursor: pointer;
      flex-shrink: 0;
    }

    input[class*="_checkboxInput_"]:checked {
      background-color: var(--color-foreground);
      border-color: var(--color-foreground);
    }

    input[class*="_checkboxInput_"]:checked::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: var(--checkbox-size);
      height: var(--checkbox-size);
      background-color: var(--color-background);
      mask-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M4.75439 10.7485L7.68601 14.5888C7.79288 14.7288 7.84632 14.7988 7.91174 14.8242C7.96907 14.8466 8.03262 14.8469 8.09022 14.8253C8.15596 14.8007 8.21026 14.7314 8.31886 14.5927L15.2475 5.74658' stroke='black' stroke-width='1' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
      mask-size: contain;
      mask-repeat: no-repeat;
      mask-position: center;
    }

    button[class*="_formSubmitButton_"] {
      font-family: var(--button-font-family-primary) !important;
      font-size: var(--font-paragraph--size) !important;
      font-weight: var(--font-paragraph--weight) !important;
      font-style: var(--font-paragraph--style) !important;
      line-height: var(--font-paragraph--line-height) !important;
      text-transform: var(--button-text-case-primary) !important;
      text-align: center !important;
      padding-block: var(--button-padding-block);
      padding-inline: var(--button-padding-inline);
      height: auto;
      width: 100% !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      margin-top: clamp(24px, 2.4vw, 32px) !important;
      transform: translateY(-0.15em) !important;
      box-shadow: inset 0 0 0 1px var(--color-foreground), 0 0.15em 0 0 #000 !important;
      transition: transform 0.1s, box-shadow 0.1s !important;
    }

    button[class*="_formSubmitButton_"]:hover,
    button[class*="_formSubmitButton_"]:active {
      transform: translateY(0) !important;
      box-shadow: inset 0 0 0 1px var(--color-foreground), 0 0 0 0 #000 !important;
    }

    /* Underline style, testing a look that matches the checklist rows
       used elsewhere on product pages, instead of the pill/box fields. */
    input[class*="_formInputField_"] {
      border-bottom: 1px solid var(--color-foreground) !important;
      background-color: transparent !important;
      padding-right: 44px !important;
    }

    input[class*="_formPhoneInputField_"] {
      border-bottom: 1px solid var(--color-foreground) !important;
      background-color: transparent !important;
    }

    div[class*="_formPhoneInputContainer_"] {
      display: flex;
      align-items: flex-end;
      gap: 12px;
    }

    .theme-field-valid-check {
      display: none;
      position: absolute !important;
      right: 0 !important;
      top: auto !important;
      bottom: 14px !important;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border: 1px solid var(--color-foreground);
      border-radius: 50%;
      color: var(--color-foreground);
      pointer-events: none;
    }

    input[class*="_formInputField_"] {
      padding-bottom: 12px !important;
    }

    .theme-field-valid-check.is-visible {
      display: flex;
    }

    .theme-field-valid-check svg {
      width: 13px;
      height: auto;
    }

    /* Country select: underline like the other fields, with a circular
       chevron button in place of the app's default arrow. */
    button[class*="_selectToggle_"] {
      border-bottom: 1px solid var(--color-foreground) !important;
      background-color: transparent !important;
      border-radius: 0 !important;
      display: flex !important;
      align-items: center !important;
      justify-content: space-between !important;
      gap: 12px !important;
      padding-bottom: 10px !important;
    }

    span[class*="_arrowDownIcon_"] {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      align-self: center !important;
      width: 28px !important;
      height: 28px !important;
      margin: 0 !important;
      border: none !important;
      border-radius: 0 !important;
      color: var(--color-foreground) !important;
      flex-shrink: 0 !important;
    }

    span[class*="_arrowDownIcon_"] svg {
      width: 22px !important;
      height: 22px !important;
      margin: 0 !important;
    }

    [class*="_formDisclaimer_"] {
      font-size: clamp(15px, 1vw, 17px) !important;
      opacity: 1 !important;
      text-align: center !important;
    }

    [class*="_formDisclaimer_"] a {
      color: var(--color-foreground) !important;
      text-decoration-line: underline !important;
      text-decoration-color: var(--color-foreground) !important;
    }

    .theme-org-lookup-status {
      position: absolute;
      left: calc(9ch + 12px);
      right: 32px;
      bottom: 18px;
      font-size: 13px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      color: var(--color-foreground);
      opacity: 0.6;
      pointer-events: none;
    }

    [class*="_textHeading_"] {
      margin-bottom: clamp(16px, 1.8vw, 24px) !important;
    }

    .theme-tax-id-hint {
      font-family: var(--font-h3--family);
      font-style: var(--font-h3--style);
      font-size: clamp(18px, 2.34vw, 30px);
      color: var(--color-foreground);
      margin-bottom: clamp(4px, 0.6vw, 8px);
    }

    section[class*="_formBannerContainer_"] {
      gap: 24px !important;
    }

    .theme-success-heading {
      font-family: var(--font-h2--family) !important;
      font-style: var(--font-h2--style) !important;
      font-weight: var(--font-h2--weight) !important;
      font-size: var(--font-h2--size) !important;
      color: var(--color-foreground) !important;
    }

    .theme-success-body {
      font-family: var(--font-paragraph--family) !important;
      font-style: var(--font-paragraph--style) !important;
      font-weight: var(--font-paragraph--weight) !important;
      font-size: var(--font-paragraph--size) !important;
      line-height: var(--font-paragraph--line-height) !important;
      color: var(--color-foreground) !important;
    }

    .theme-success-cta {
      margin-top: 24px;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .theme-success-cta p {
      font-family: var(--font-paragraph--family) !important;
      font-size: var(--font-paragraph--size) !important;
      line-height: var(--font-paragraph--line-height) !important;
      color: var(--color-foreground) !important;
      margin: 0 !important;
    }

    .theme-success-cta a {
      color: var(--color-foreground) !important;
      text-decoration-line: underline !important;
      text-decoration-color: var(--color-foreground) !important;
    }
  `;
  root.appendChild(style);
}

function updateDisclaimer(root) {
  const disclaimer = root.querySelector('[class*="_formDisclaimer_"] p');
  if (!disclaimer || disclaimer.dataset.themeTextUpdated) return;
  disclaimer.dataset.themeTextUpdated = 'true';
  disclaimer.innerHTML =
    'By submitting this form you agree to our ' +
    '<a href="https://eatmygoodness.com/policies/privacy-policy">privacy policy</a> and ' +
    '<a href="https://eatmygoodness.com/policies/terms-of-service">terms of service</a>.';
}

// The post-submit confirmation message's markup isn't documented, and we
// can't trigger a real submission ourselves to inspect it (that would create
// a fake lead in the merchant's Forms app), so we find it by its fixed copy
// text instead of a class name, which also survives the app's own CSS
// module hashes changing between deploys.
function styleSuccessMessage(root) {
  const heading = Array.from(root.querySelectorAll('h1, h2, h3, h4, h5, h6, div, span, p')).find(
    (el) => el.children.length === 0 && el.textContent.trim() === 'Thank you!'
  );
  if (!heading || heading.dataset.themeSuccessStyled) return;
  heading.dataset.themeSuccessStyled = 'true';
  heading.classList.add('theme-success-heading');

  const body = heading.nextElementSibling;
  if (body) {
    body.classList.add('theme-success-body');

    const cta = document.createElement('div');
    cta.className = 'theme-success-cta';
    cta.innerHTML =
      '<p>In the meantime, <a href="https://eatmygoodness.com/collections/all">learn more about our product range here</a>.</p>' +
      '<p>If you have any more questions, don’t hesitate to <a href="https://eatmygoodness.com/pages/contact">contact us here</a>.</p>';
    body.insertAdjacentElement('afterend', cta);
  }
}

function addTaxIdHint(root) {
  const taxIdInput = root.querySelector('#company_tax_id');
  if (!taxIdInput || taxIdInput.dataset.hintAdded) return;
  taxIdInput.dataset.hintAdded = 'true';

  const hint = document.createElement('div');
  hint.className = 'theme-tax-id-hint';
  hint.textContent = 'Start here — we will fill in what we can for you.';
  taxIdInput.parentElement.insertAdjacentElement('beforebegin', hint);
}

function renameRecipientLabels(root) {
  root.querySelectorAll('input[id$="_recipient"]').forEach((input) => {
    const label = input.parentElement?.querySelector('label');
    if (label && !label.dataset.themeTextUpdated) {
      label.dataset.themeTextUpdated = 'true';
      label.textContent = 'Company';
    }
  });
}

// Mirrors the top-level contact name into the billing address's own
// first/last name fields as the person types, so they don't have to enter
// their name twice — those fields stay visible, matching Shopify's own
// backend field structure, but most people filling this in are naming
// themselves in both places anyway.
function mirrorContactNameToBilling(root) {
  const firstName = root.querySelector('#first_name');
  const lastName = root.querySelector('#last_name');
  const billingFirstName = root.querySelector('#billing_address_first_name');
  const billingLastName = root.querySelector('#billing_address_last_name');
  if (!firstName || !lastName || !billingFirstName || !billingLastName) return;
  if (firstName.dataset.mirrorToBillingAttached) return;
  firstName.dataset.mirrorToBillingAttached = 'true';
  lastName.dataset.mirrorToBillingAttached = 'true';

  const sync = () => {
    if (!billingFirstName.value) setNativeInputValue(billingFirstName, firstName.value);
    if (!billingLastName.value) setNativeInputValue(billingLastName, lastName.value);
  };
  firstName.addEventListener('input', sync);
  lastName.addEventListener('input', sync);
}

function setNativeInputValue(input, value) {
  const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
  setter.call(input, value);
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('blur', { bubbles: true }));
}

async function lookupOrgNumber(root, orgnr, statusEl) {
  statusEl.textContent = 'Looking up company…';
  try {
    const res = await fetch(`https://data.brreg.no/enhetsregisteret/api/enheter/${orgnr}`);
    if (!res.ok) {
      statusEl.textContent = res.status === 404 ? 'No company found for this number' : '';
      return;
    }
    const data = await res.json();
    const companyNameInput = root.querySelector('#company_name');
    const address1Input = root.querySelector('#billing_address_address1');
    const zipInput = root.querySelector('#billing_address_zip');
    const cityInput = root.querySelector('#billing_address_city');

    const recipientInput = root.querySelector('#billing_address_recipient');
    if (data.navn && companyNameInput) setNativeInputValue(companyNameInput, data.navn);
    if (data.navn && recipientInput) setNativeInputValue(recipientInput, data.navn);

    const addr = data.forretningsadresse || data.postadresse;
    if (addr) {
      if (addr.adresse && addr.adresse.length && address1Input) {
        setNativeInputValue(address1Input, addr.adresse.join(' '));
      }
      if (addr.postnummer && zipInput) setNativeInputValue(zipInput, addr.postnummer);
      if (addr.poststed && cityInput) setNativeInputValue(cityInput, addr.poststed);
    }
    statusEl.textContent = 'Company found — details filled in below';
  } catch (e) {
    statusEl.textContent = '';
  }
}

function attachOrgLookup(root) {
  const taxIdInput = root.querySelector('#company_tax_id');
  if (!taxIdInput || taxIdInput.dataset.orgLookupAttached) return;
  taxIdInput.dataset.orgLookupAttached = 'true';

  const statusEl = document.createElement('div');
  statusEl.className = 'theme-org-lookup-status';
  taxIdInput.parentElement.appendChild(statusEl);

  let debounceTimer;
  taxIdInput.addEventListener('input', () => {
    clearTimeout(debounceTimer);
    statusEl.textContent = '';
    const raw = taxIdInput.value.replace(/\s/g, '');
    if (!/^\d{9}$/.test(raw)) return;
    debounceTimer = setTimeout(() => lookupOrgNumber(root, raw, statusEl), 500);
  });
}

const CHECK_SVG = `<svg width="17" height="13" viewBox="0 0 17 13" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.82005 12.2514C6.68293 12.2509 6.54737 12.2223 6.42179 12.1672C6.29621 12.1122 6.18329 12.0319 6.09005 11.9314L1.23005 6.76138C1.04837 6.56777 0.951052 6.30992 0.959491 6.04456C0.96793 5.77919 1.08144 5.52805 1.27505 5.34638C1.46865 5.1647 1.7265 5.06738 1.99187 5.07582C2.25723 5.08426 2.50837 5.19777 2.69005 5.39138L6.81005 9.78137L15.22 0.581375C15.3054 0.475098 15.4116 0.38739 15.532 0.323639C15.6525 0.259887 15.7848 0.221439 15.9206 0.210654C16.0565 0.19987 16.1932 0.216978 16.3222 0.260926C16.4512 0.304875 16.5699 0.374737 16.6709 0.466221C16.772 0.557705 16.8532 0.66888 16.9098 0.792918C16.9663 0.916955 16.9968 1.05124 16.9996 1.18751C17.0023 1.32379 16.9771 1.45919 16.9256 1.58539C16.8741 1.71158 16.7973 1.82592 16.7 1.92138L7.56005 11.9214C7.46769 12.0237 7.35516 12.1059 7.22954 12.1627C7.10391 12.2195 6.9679 12.2497 6.83005 12.2514H6.82005Z" fill="currentColor"></path></svg>`;

const CHEVRON_SVG = `<svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.5 8.25L10 11.75L13.5 8.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

function replaceArrowIcon(root) {
  root.querySelectorAll('span[class*="_arrowDownIcon_"]').forEach((el) => {
    if (el.dataset.themeIconReplaced) return;
    el.dataset.themeIconReplaced = 'true';
    el.innerHTML = CHEVRON_SVG;
  });
}

function attachValidityChecks(root) {
  const inputs = root.querySelectorAll('input[class*="_formInputField_"]');
  inputs.forEach((input) => {
    if (input.dataset.validCheckAttached) return;
    input.dataset.validCheckAttached = 'true';

    const container = input.parentElement;
    if (!container) return;
    const computedPosition = getComputedStyle(container).position;
    if (computedPosition === 'static') {
      container.style.position = 'relative';
    }

    const check = document.createElement('span');
    check.className = 'theme-field-valid-check';
    check.setAttribute('aria-hidden', 'true');
    check.innerHTML = CHECK_SVG;
    container.appendChild(check);

    // These are plain type="text" inputs with no native constraints — the
    // app validates its own React state at submit time, not via the DOM —
    // so email needs its own format check rather than just "has a value".
    const labelText = (container.querySelector('label')?.textContent || '').toLowerCase();
    const isEmailField = labelText.includes('email');
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const updateCheck = () => {
      const value = input.value.trim();
      const isValid = isEmailField ? emailPattern.test(value) : value !== '';
      check.classList.toggle('is-visible', isValid);
    };

    input.addEventListener('input', updateCheck);
    input.addEventListener('blur', updateCheck);
    input.addEventListener('change', updateCheck);
    updateCheck();
  });
}

function watchEmbed(embed) {
  if (embed.dataset.themeOverridesWatched) return;
  embed.dataset.themeOverridesWatched = 'true';

  const attachObserver = () => {
    injectFormsStyles(embed.shadowRoot);
    attachValidityChecks(embed.shadowRoot);
    replaceArrowIcon(embed.shadowRoot);
    updateDisclaimer(embed.shadowRoot);
    addTaxIdHint(embed.shadowRoot);
    renameRecipientLabels(embed.shadowRoot);
    mirrorContactNameToBilling(embed.shadowRoot);
    attachOrgLookup(embed.shadowRoot);
    styleSuccessMessage(embed.shadowRoot);
    new MutationObserver(() => {
      injectFormsStyles(embed.shadowRoot);
      attachValidityChecks(embed.shadowRoot);
      replaceArrowIcon(embed.shadowRoot);
      updateDisclaimer(embed.shadowRoot);
      addTaxIdHint(embed.shadowRoot);
      renameRecipientLabels(embed.shadowRoot);
      mirrorContactNameToBilling(embed.shadowRoot);
      attachOrgLookup(embed.shadowRoot);
      styleSuccessMessage(embed.shadowRoot);
    }).observe(embed.shadowRoot, {
      childList: true,
      subtree: true,
    });
  };

  if (embed.shadowRoot) {
    attachObserver();
    return;
  }

  // The shadow root may attach asynchronously after the element itself
  // appears in the DOM, so poll briefly until it's available.
  let attempts = 0;
  const poll = setInterval(() => {
    attempts += 1;
    if (embed.shadowRoot) {
      clearInterval(poll);
      attachObserver();
    } else if (attempts > 40) {
      clearInterval(poll);
    }
  }, 100);
}

function scan() {
  document.querySelectorAll('shopify-forms-embed').forEach(watchEmbed);
}

scan();

// The app embed can be inserted into the page after this script runs, so
// keep watching the document for it.
new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
